import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { WP_FAQ_CATEGORIES } from "@/content/work-permit-corpus/faq-defs";

export const Route = createFileRoute("/en/work-permit/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/work-permit-corpus");
    return { faqs: m.WP_FAQ_EN };
  },
  component: WpFaqHubEn,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Thailand Work Permit FAQ — ${faqs.length} Answers with Official Sources`,
        description:
          "Thailand work permit FAQ for foreigners — employer capital and Thai-staff ratio, Department of Employment forms and process, Non-B visa link, renewals, BOI One Stop Service, prohibited occupations and penalties. Referenced to doe.go.th and immigration.go.th.",
        path: "/en/work-permit/faq",
        type: "article",
        alternates: [{ lang: "th", path: "/work-permit/faq" }],
        keywords: ["thailand work permit", "non-b visa", "department of employment", "work permit renewal", "BOI one stop service"],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Work Permit FAQ", url: `${site}/en/work-permit/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/work-permit/faq")) },
      ],
    };
  },
});

function WpFaqHubEn() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Work Permit FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">Thailand Work Permit — {faqs.length} Frequently Asked Questions</h1>
        <p className="mb-6 text-muted-foreground">
          Answers follow the Emergency Decree on the Management of Foreign Workers' Employment B.E. 2560 (2017) and its
          2018 amendment, with guidance from the Department of Employment (doe.go.th), the Immigration Bureau
          (immigration.go.th), BOI (boi.go.th), the Social Security Office and the Revenue Department.
        </p>
        <div className="mb-8 rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
          <strong className="text-foreground">Please note:</strong> a work permit is tied to the named employer, position
          and workplace. Every approval is at the officer's discretion — we do not guarantee outcomes. Fees and timelines
          follow published schedules and may change; reconfirm with the authority before filing.
        </div>
        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href="/work-permit/faq" className="text-primary underline">FAQ ใบอนุญาตทำงาน →</a>
        </div>
        <section aria-label="Work permit FAQ categories" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">Browse by topic</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {WP_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/en/work-permit/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary">
                <div className="mb-1 font-semibold">{c.titleEn}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">Q{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <section className="mt-12 rounded-lg border bg-primary/5 p-6">
          <h2 className="mb-2 text-xl font-bold">Talk to our team</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Ask about employer eligibility and service fees for your own case by phone, LINE or email — Mon–Sat 9:00–18:00 ICT.
          </p>
          <a href="/en/contact" className="inline-block rounded-md bg-primary px-5 py-2 font-medium text-primary-foreground">Contact us →</a>
        </section>
        <FaqSourceNote topic="work-permit" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}